'use client';

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  FileText,
  Clock,
  Wallet,
  UserCog,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { buildBackendUrl } from '@textile-erp/core';
import {
  useNotifications,
  type PendingApprovalItem,
  type PendingApprovalType
} from '../providers/NotificationProvider';

interface PendingApprovalsNotificationBellProps {
  className?: string;
}

// Config for each approval type
const typeConfig: Record<PendingApprovalType, {
  icon: typeof FileText;
  color: string;
  bgColor: string;
  label: string;
  route: string;
  paramKey: string;
}> = {
  LOAN: {
    icon: FileText,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30',
    label: 'Loan',
    route: '/payroll/loan-approval',
    paramKey: 'loanId',
  },
  PERMISSION: {
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Permission',
    route: '/payroll/permission-approval',
    paramKey: 'permissionId',
  },
  ADVANCE: {
    icon: Wallet,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-100 dark:bg-green-900/30',
    label: 'Advance',
    route: '/payroll/advance-approval',
    paramKey: 'advanceId',
  },
  JOB_CHANGE: {
    icon: UserCog,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-100 dark:bg-purple-900/30',
    label: 'Job Change',
    route: '/payroll/employee-job-detail-change-approval',
    paramKey: 'jobChangeId',
  },
};

export function PendingApprovalsNotificationBell({ className }: PendingApprovalsNotificationBellProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    counts,
    pendingApprovals,
    hasMore,
    loading,
    fetchPendingApprovals,
  } = useNotifications();

  // Open dropdown and fetch approvals
  const handleToggle = async () => {
    if (!isOpen) {
      // Calculate position before opening
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          right: window.innerWidth - rect.right,
        });
      }
      // Fetch latest pending approvals
      await fetchPendingApprovals();
    }
    setIsOpen(!isOpen);
  };

  // Navigate to specific approval
  const handleItemClick = (item: PendingApprovalItem) => {
    setIsOpen(false);
    const config = typeConfig[item.type];
    navigate(`${config.route}?${config.paramKey}=${item.id}`);
  };

  // Navigate to view all of specific type
  const handleViewAllType = (type: PendingApprovalType) => {
    setIsOpen(false);
    const config = typeConfig[type];
    navigate(`${config.route}?status=PENDING`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get icon component for type
  const getTypeIcon = (type: PendingApprovalType) => {
    const config = typeConfig[type];
    const IconComponent = config.icon;
    return <IconComponent className={`w-4 h-4 ${config.color}`} />;
  };

  // Get employee initials for avatar fallback
  const getInitials = (name: string) => {
    const names = name.trim().split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Get profile picture URL
  const getProfilePictureUrl = (profilePictureUrl: string | null) => {
    if (!profilePictureUrl) return null;
    // If it's already a full URL, return as is
    if (profilePictureUrl.startsWith('http')) return profilePictureUrl;
    // Otherwise, prepend the backend URL
    return buildBackendUrl(profilePictureUrl);
  };

  return (
    <>
      {/* Notification Bell Button */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`relative p-2 rounded-lg hover:bg-theme-primary/10 transition-colors group ${className || ''}`}
        title="Pending Approvals"
      >
        <Bell className="w-5 h-5 text-theme-tertiary group-hover:text-theme-secondary transition-colors" />

        {/* Badge */}
        {counts.total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 animate-pulse">
            {counts.total > 99 ? '99+' : counts.total}
          </span>
        )}
      </button>

      {/* Dropdown Portal */}
      {isOpen && typeof window !== 'undefined' && createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-96 bg-theme-primary border border-theme-primary/20 rounded-2xl shadow-2xl z-[9999] overflow-hidden animate-fadeInScale"
          style={{
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
          }}
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-theme-primary/10 bg-theme-secondary/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-theme-secondary" />
                <span className="font-semibold text-theme-primary text-sm">
                  Pending Approvals
                </span>
              </div>
              {counts.total > 0 && (
                <span className="text-xs font-medium text-white bg-red-500 px-2 py-0.5 rounded-full">
                  {counts.total}
                </span>
              )}
            </div>

            {/* Type counts summary */}
            {counts.total > 0 && (
              <div className="flex gap-3 mt-2 text-xs">
                {counts.loan > 0 && (
                  <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                    <FileText className="w-3 h-3" /> {counts.loan}
                  </span>
                )}
                {counts.permission > 0 && (
                  <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <Clock className="w-3 h-3" /> {counts.permission}
                  </span>
                )}
                {counts.advance > 0 && (
                  <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                    <Wallet className="w-3 h-3" /> {counts.advance}
                  </span>
                )}
                {counts.jobChange > 0 && (
                  <span className="flex items-center gap-1 text-purple-600 dark:text-purple-400">
                    <UserCog className="w-3 h-3" /> {counts.jobChange}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-theme-tertiary animate-spin" />
              </div>
            ) : pendingApprovals.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-3">
                  <Bell className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-medium text-theme-primary">No Pending Approvals</p>
                <p className="text-xs text-theme-tertiary mt-1">All requests have been processed</p>
              </div>
            ) : (
              <div className="py-1">
                {pendingApprovals.map((item) => {
                  const config = typeConfig[item.type];
                  const profilePicUrl = getProfilePictureUrl(item.employee.profilePictureUrl);
                  return (
                    <button
                      key={`${item.type}-${item.id}`}
                      onClick={() => handleItemClick(item)}
                      className="w-full px-4 py-3 hover:bg-theme-secondary/50 transition-colors text-left group/item"
                    >
                      <div className="flex items-start gap-3">
                        {/* Employee Profile Picture */}
                        <div className="relative flex-shrink-0">
                          {profilePicUrl ? (
                            <img
                              src={profilePicUrl}
                              alt={item.employee.name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-theme-primary/10"
                              onError={(e) => {
                                // Hide image on error and show fallback
                                (e.target as HTMLImageElement).style.display = 'none';
                                const fallback = (e.target as HTMLImageElement).nextElementSibling;
                                if (fallback) (fallback as HTMLElement).style.display = 'flex';
                              }}
                            />
                          ) : null}
                          {/* Fallback Avatar with Initials */}
                          <div
                            className={`w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold ${profilePicUrl ? 'hidden' : 'flex'}`}
                            style={{ display: profilePicUrl ? 'none' : 'flex' }}
                          >
                            {getInitials(item.employee.name)}
                          </div>
                          {/* Type badge indicator */}
                          <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full ${config.bgColor} flex items-center justify-center border-2 border-white dark:border-gray-800`}>
                            {getTypeIcon(item.type)}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-theme-primary truncate">
                              {item.employee.name}
                            </p>
                            <ChevronRight className="w-4 h-4 text-theme-tertiary group-hover/item:text-theme-secondary transition-colors flex-shrink-0" />
                          </div>
                          <p className="text-xs text-theme-tertiary mt-0.5">
                            {item.employee.code}
                          </p>
                          <div className="flex items-center justify-between mt-1">
                            <span className={`text-xs font-medium ${config.color}`}>
                              {item.description}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded ${config.bgColor} ${config.color}`}>
                              {config.label}
                            </span>
                            <span className="text-xs text-theme-tertiary">
                              {formatDate(item.date)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - Quick Links */}
          {(pendingApprovals.length > 0 || hasMore) && (
            <div className="border-t border-theme-primary/10 p-2">
              <p className="text-[10px] text-theme-tertiary uppercase tracking-wide px-2 mb-1">View All By Type</p>
              <div className="flex flex-wrap gap-1">
                {counts.loan > 0 && (
                  <button
                    onClick={() => handleViewAllType('LOAN')}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 dark:text-orange-400 transition-colors"
                  >
                    <FileText className="w-3 h-3" />
                    Loans ({counts.loan})
                  </button>
                )}
                {counts.permission > 0 && (
                  <button
                    onClick={() => handleViewAllType('PERMISSION')}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors"
                  >
                    <Clock className="w-3 h-3" />
                    Permissions ({counts.permission})
                  </button>
                )}
                {counts.advance > 0 && (
                  <button
                    onClick={() => handleViewAllType('ADVANCE')}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 transition-colors"
                  >
                    <Wallet className="w-3 h-3" />
                    Advances ({counts.advance})
                  </button>
                )}
                {counts.jobChange > 0 && (
                  <button
                    onClick={() => handleViewAllType('JOB_CHANGE')}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400 transition-colors"
                  >
                    <UserCog className="w-3 h-3" />
                    Job Changes ({counts.jobChange})
                  </button>
                )}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  );
}
